import { useState } from "react";
import { monthlyPicks, MonthlyPick } from "@/mocks/brands";
import { products, Product } from "@/mocks/products";
import ProductDetailModal from "@/components/base/ProductDetailModal";

/* ── 편집 모달 ── */
interface EditPickModalProps {
  picks: MonthlyPick[];
  onSave: (picks: MonthlyPick[]) => void;
  onClose: () => void;
}

const EditPickModal = ({ picks, onSave, onClose }: EditPickModalProps) => {
  const [draft, setDraft] = useState<MonthlyPick[]>(picks.map((p) => ({ ...p })));

  const updateReason = (idx: number, reason: string) => {
    setDraft((prev) => prev.map((p, i) => i === idx ? { ...p, reason } : p));
  };

  const updateProduct = (idx: number, productId: string) => {
    setDraft((prev) => prev.map((p, i) => i === idx ? { ...p, productId } : p));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: "rgba(0,0,0,0.5)" }} onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-stone-100">
          <div>
            <h3 className="text-sm font-black text-stone-900">이달의 인기제품 수정</h3>
            <p className="text-xs text-stone-400 mt-0.5">제품과 추천 이유를 변경할 수 있어요</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-stone-100 cursor-pointer">
            <i className="ri-close-line text-stone-500" />
          </button>
        </div>
        <div className="px-6 py-5 space-y-4 max-h-[60vh] overflow-y-auto">
          {draft.map((pick, idx) => (
            <div key={idx} className="bg-stone-50 rounded-xl p-4 space-y-3">
              <div className="flex items-center gap-2">
                <span className="w-6 h-6 flex items-center justify-center bg-stone-900 text-white text-xs font-black rounded-full">{pick.rank}</span>
                <span className="text-xs font-bold text-stone-700">순위 {pick.rank}</span>
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-stone-500 mb-1">제품 선택</label>
                <select
                  value={pick.productId}
                  onChange={(e) => updateProduct(idx, e.target.value)}
                  className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm text-stone-800 outline-none focus:border-stone-400 bg-white cursor-pointer"
                >
                  {products.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-stone-500 mb-1">추천 이유</label>
                <input
                  value={pick.reason}
                  onChange={(e) => updateReason(idx, e.target.value)}
                  className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm text-stone-800 outline-none focus:border-stone-400"
                  placeholder="한 줄 추천 이유"
                />
              </div>
            </div>
          ))}
        </div>
        <div className="px-6 pb-5 flex gap-2">
          <button onClick={onClose} className="flex-1 py-2.5 border border-stone-200 text-stone-600 text-sm font-semibold rounded-xl hover:bg-stone-50 cursor-pointer whitespace-nowrap">취소</button>
          <button onClick={() => { onSave(draft); onClose(); }} className="flex-1 py-2.5 bg-stone-900 text-white text-sm font-bold rounded-xl hover:bg-stone-700 cursor-pointer whitespace-nowrap">저장하기</button>
        </div>
      </div>
    </div>
  );
};

/* ── 인기제품 카드 ── */
const RANK_STYLES = [
  "text-amber-500",
  "text-stone-400",
  "text-amber-700",
];

const PickCard = ({ pick, product, onDetail }: { pick: MonthlyPick; product: Product; onDetail: () => void }) => (
  <div
    className="group bg-white rounded-2xl border border-stone-100 hover:border-stone-200 overflow-hidden transition-all duration-300 hover:-translate-y-1 cursor-pointer"
    onClick={onDetail}
  >
    {/* Image */}
    <div className="relative overflow-hidden" style={{ aspectRatio: "4/3" }}>
      <img src={product.image} alt={product.name} className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105" />
      {/* Rank badge */}
      <div className="absolute top-3 left-3 w-9 h-9 flex items-center justify-center bg-white rounded-full">
        <span className={`text-lg font-black ${RANK_STYLES[pick.rank - 1] || "text-stone-600"}`} style={{ fontFamily: "'Playfair Display', serif" }}>
          {pick.rank}
        </span>
      </div>
      {product.badge && (
        <div className="absolute top-3 right-3">
          <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full ${product.badge === "BEST" ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>
            {product.badge}
          </span>
        </div>
      )}
    </div>
    {/* Info */}
    <div className="p-4">
      <p className="text-[10px] font-bold text-stone-400 tracking-widest uppercase mb-1">{product.category}</p>
      <h3 className="text-sm font-black text-stone-900 mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>{product.name}</h3>
      <div className="flex items-center gap-1 mb-2">
        {[1,2,3,4,5].map((s) => (
          <div key={s} className="w-3 h-3 flex items-center justify-center">
            <i className={`text-[10px] ${s <= Math.floor(product.rating) ? "ri-star-fill text-amber-400" : "ri-star-line text-stone-300"}`} />
          </div>
        ))}
        <span className="text-xs text-stone-500 ml-1">{product.rating}</span>
      </div>
      <div className="flex items-center gap-1.5 bg-stone-50 rounded-full px-3 py-1.5 w-fit">
        <i className="ri-award-line text-stone-500 text-xs" />
        <span className="text-[11px] font-semibold text-stone-600">{pick.reason}</span>
      </div>
    </div>
  </div>
);

/* ── 메인 섹션 ── */
const MonthlyPicksSection = () => {
  const [picks, setPicks] = useState<MonthlyPick[]>(monthlyPicks);
  const [showEdit, setShowEdit] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);

  const pickProducts = picks
    .map((pick) => ({ pick, product: products.find((p) => p.id === pick.productId) }))
    .filter((item): item is { pick: MonthlyPick; product: Product } => !!item.product);

  return (
    <div className="mb-16">
      {/* Header */}
      <div className="flex items-end justify-between mb-6">
        <div>
          <p className="text-xs font-bold tracking-widest uppercase text-stone-400 mb-1">This Month</p>
          <h2 className="text-2xl font-black text-stone-900" style={{ fontFamily: "'Playfair Display', serif" }}>
            이달의 인기제품
          </h2>
        </div>
        <button
          onClick={() => setShowEdit(true)}
          className="flex items-center gap-1.5 border border-stone-200 text-stone-600 text-xs font-semibold px-3.5 py-2 rounded-full hover:bg-stone-50 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-edit-line text-xs" />
          순위 수정
        </button>
      </div>

      {/* Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
        {pickProducts.map(({ pick, product }) => (
          <PickCard key={pick.productId} pick={pick} product={product} onDetail={() => setSelectedProduct(product)} />
        ))}
      </div>

      {showEdit && (
        <EditPickModal picks={picks} onSave={setPicks} onClose={() => setShowEdit(false)} />
      )}
      <ProductDetailModal product={selectedProduct} onClose={() => setSelectedProduct(null)} />
    </div>
  );
};

export default MonthlyPicksSection;
